import { useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUturnLeftIcon, ChatBubbleBottomCenterTextIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, EmptyState, Loading, Textarea } from "../../ui";
import { errorMessageOf, scholarshipsApi } from "../../../api/scholarships";
import { REVIEW_KEY, formatDateTime, formatScore } from "./model";

// Review comments (draft 21): plain text, never edited; a correction is a new linked comment
export const COMMENT_MAX = 1000;
const ROLE_NAMES = { HEC_EVALUATOR: "Evaluator", SCHOLARSHIP_ADMIN: "Scholarship Admin" };

export const reviewHistoryKey = (path, applicationId) => [...REVIEW_KEY, "review-history", path, applicationId];

/** `path` is "admin" (Admin, DG, Minister: everything) or "evaluator" (the caller's own). */
export const useReviewHistory = (path, applicationId, options = {}) =>
  useQuery({
    queryKey: reviewHistoryKey(path, applicationId),
    queryFn: async () => (await scholarshipsApi.get(`${path}/applications/${applicationId}/review-history`)).data,
    ...options,
  });

/** Who wrote a comment and when, then its text exactly as written (as text, never as HTML). */
export function CommentText({ comment, className = "" }) {
  return (
    <div className={className}>
      <p className="whitespace-pre-wrap break-words text-sm text-slate-800">{comment.text}</p>
      <p className="mt-1 text-xs text-slate-600">
        {comment.author?.name ?? "Unknown user"}
        {ROLE_NAMES[comment.authorRole] && ` (${ROLE_NAMES[comment.authorRole]})`} · <time dateTime={comment.createdAt}>{formatDateTime(comment.createdAt)}</time>
      </p>
    </div>
  );
}

/** A comment with its corrections, oldest first, and optionally a way to add one. */
function Comment({ comment, path, applicationId, canCorrect }) {
  return (
    <div className="flex flex-col gap-2">
      <CommentText comment={comment} />
      {comment.corrections?.length > 0 && (
        <ul className="flex flex-col gap-2 border-l-2 border-amber-300 pl-3" aria-label="Corrections">
          {comment.corrections.map((c) => (
            <li key={c.id}>
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Correction</p>
              <CommentText comment={c} />
            </li>
          ))}
        </ul>
      )}
      {canCorrect && <CorrectionForm path={path} applicationId={applicationId} comment={comment} />}
    </div>
  );
}

function CorrectionForm({ path, applicationId, comment }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const error = tried && !text.trim() ? "Write the correction." : undefined;

  const save = async () => {
    setTried(true);
    if (!text.trim()) return;
    setBusy(true);
    try {
      await scholarshipsApi.post(`${path}/applications/${applicationId}/review-comments/${comment.id}/corrections`, { text: text.trim() });
      await queryClient.invalidateQueries({ queryKey: reviewHistoryKey(path, applicationId) });
      toast.success("Correction added");
      setOpen(false);
      setText("");
      setTried(false);
    } catch (err) {
      console.error(`Failed to add a correction to comment ${comment.id}:`, err);
      toast.error(errorMessageOf(err, "The correction was not saved. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  if (!open)
    return (
      <Button variant="ghost" size="sm" className="self-start" icon={<PencilSquareIcon className="h-4 w-4" />} onClick={() => setOpen(true)}>
        Add a correction
      </Button>
    );
  return (
    <div className="flex flex-col gap-2 rounded-md bg-slate-50 p-3">
      <Textarea
        label="Correction"
        required
        rows={2}
        maxLength={COMMENT_MAX}
        value={text}
        onChange={(e) => setText(e.target.value)}
        info={`${text.length} of ${COMMENT_MAX} characters. The original comment stays as it is; the correction is shown under it.`}
        error={error}
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </Button>
        <Button size="sm" onClick={save} loading={busy}>
          Save correction
        </Button>
      </div>
    </div>
  );
}

/** The revert comment that sent a round back: what to look at again, from whom, when. */
export function RevertNote({ comment, path, applicationId, canCorrect, heading = "Sent back for re-marking" }) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
      <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-amber-900">
        <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
        {heading}
      </p>
      <Comment comment={comment} path={path} applicationId={applicationId} canCorrect={canCorrect} />
    </div>
  );
}

/**
 * The review history: rounds in the order they were submitted, each with its marks, comments per
 * criterion and the revert comment that ended it. "No comments yet" and "could not be loaded" are
 * different states; the second offers a retry.
 */
export default function ReviewHistory({ applicationId, path = "admin", canCorrect = () => false, query: given }) {
  const own = useReviewHistory(path, applicationId, { enabled: !given });
  const { data, isPending, isError, refetch } = given ?? own;
  const headingId = useId();

  if (isPending) return <Loading label="Loading the review history…" />;
  if (isError)
    return (
      <div role="alert">
        <EmptyState title="Comments could not be loaded" description="Check your connection and try again." onRefresh={refetch} />
      </div>
    );
  if (!data.rounds.length) return <EmptyState title="No comments yet" description="Marks and comments appear here once an evaluator submits." />;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <h3 id={headingId} className="sr-only">
        Review history
      </h3>
      <ol className="flex flex-col gap-4">
        {data.rounds.map((round) => (
          <li key={round.id} className="rounded-lg border border-slate-200 p-4">
            <h4 className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
              <span className="font-semibold text-slate-900">
                {round.evaluator.name}, round {round.roundNo}
              </span>
              <span className="text-slate-600">
                submitted <time dateTime={round.submittedAt}>{formatDateTime(round.submittedAt)}</time> · total {formatScore(round.weightedTotal)} / 100
              </span>
            </h4>
            <dl className="mt-3 flex flex-col gap-3">
              {round.marks.map((m) => (
                <div key={m.criterionId} className="grid gap-1 sm:grid-cols-[12rem_1fr]">
                  <dt className="text-sm font-medium text-slate-700">
                    {m.criterionName ?? "Criterion"} <span className="font-normal text-slate-600">· {formatScore(m.marks)}</span>
                  </dt>
                  <dd>
                    {m.comment ? (
                      <Comment comment={m.comment} path={path} applicationId={applicationId} canCorrect={canCorrect(m.comment)} />
                    ) : (
                      <span className="flex items-center gap-1 text-sm text-slate-600">
                        <ChatBubbleBottomCenterTextIcon className="h-4 w-4" aria-hidden="true" /> No comment
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            {round.revert && (
              <div className="mt-3">
                <RevertNote comment={round.revert} path={path} applicationId={applicationId} canCorrect={canCorrect(round.revert)} />
              </div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
